import { api } from "./api";

const normalize = (value) => String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const fields = {
  resourceName: ["vmname", "virtualmachinename", "resourcename"],
  location: ["location", "region"],
  azureImageId: ["imageid", "image", "osimage"],
  architecture: ["architecture"],
  vmSizeName: ["vmsize", "vmsizename", "instancetype"],
  availabilityZone: ["availabilityzone"],
  azureVnetId: ["virtualnetwork", "vnet", "azurevnetid"],
  azureSubnetId: ["subnet", "azuresubnetid"],
  securityType: ["securitytype"],
};

const findFieldName = (formConfig, formData, names) => {
  // Try to find in formConfig by fieldName
  const configByName = formConfig.find((field) => names.includes(normalize(field.fieldName)));
  if (configByName) return configByName.fieldName;
  
  // Try to find in formConfig by fieldLabel
  const configByLabel = formConfig.find((field) => names.includes(normalize(field.fieldLabel)));
  if (configByLabel) return configByLabel.fieldName;
  
  // Try to find in formData keys (case-insensitive)
  const dataKey = Object.keys(formData).find((name) => names.includes(normalize(name)));
  if (dataKey) return dataKey;
  
  // If not found in formConfig, try to match against formData keys directly
  // This handles cases where the field exists in formData but not in formConfig
  return Object.keys(formData).find((key) => 
    names.some(name => normalize(key) === name)
  );
};

const validationError = (message, fieldName = "") =>
  Object.assign(new Error(message), { showDialog: true, fieldName });

export const mapAzureVmValidationFields = (fieldNames, formConfig, formData) =>
  String(fieldNames ?? "").split(",").map((name) => {
    const key = normalize(name);
    const aliases = Object.entries(fields).find(
      ([apiName, names]) => normalize(apiName) === key || names.includes(key)
    )?.[1] ?? [key];
    return findFieldName(formConfig, formData, aliases) || name.trim();
  }).filter(Boolean).join(",");

export const buildAzureVmValidationParams = ({
  formConfig, formData, accountId, resourceId = 0,
}) => {
  const params = {
    resourceName: "",
    resourceId,
    accountId: String(accountId ?? "").trim(),
    resourceType: "vm",
  };
  if (!params.accountId) {
    throw validationError("Please select an Azure subscription before continuing.");
  }
  if (!Number.isSafeInteger(Number(resourceId)) || Number(resourceId) < 0 ||
      String(resourceId ?? "").trim() === "") {
    throw validationError("A valid resource ID is required for Azure VM validation.");
  }
  params.resourceId = Number(resourceId);

  Object.entries(fields).forEach(([apiName, names]) => {
    const fieldName = findFieldName(formConfig, formData, names);
    
    if (!fieldName) {
      console.warn(`Field mapping failed for ${apiName}. Looking for aliases:`, names);
      throw validationError(
        `${apiName} field configuration is missing. Please contact support.`,
        apiName
      );
    }
    
    const selection = formData[fieldName];
    let value = selection?.value ?? selection;
    
    // For ID fields, try to extract numeric value from nested objects
    if (["azureImageId", "azureVnetId"].includes(apiName) && typeof value === "object" && value !== null) {
      value = value.id ?? value.value ?? value;
    }
    
    const stringValue = typeof value === "string" || typeof value === "number"
      ? String(value).trim() : "";
    const label = formConfig.find((field) => field.fieldName === fieldName)?.fieldLabel ||
      apiName;
    
    if (!stringValue) {
      throw validationError(`${label} is required for Azure VM validation.`, fieldName);
    }
    
    if (apiName === "azureImageId") {
      if (!/^[1-9]\d*$/.test(stringValue) || !Number.isSafeInteger(Number(stringValue))) {
        console.warn(`Invalid ID format for ${apiName}:`, stringValue, `Expected numeric ID, got:`, value);
        throw validationError(`Select a valid ${label}.`, fieldName);
      }
      params[apiName] = Number(stringValue);
    } else if (apiName === "azureVnetId") {
      // azureVnetId can be numeric or string (network name)
      if (/^\d+$/.test(stringValue)) {
        params[apiName] = Number(stringValue);
      } else {
        params[apiName] = stringValue;
      }
    } else {
      params[apiName] = stringValue;
    }
  });
  return params;
};

export const validateAzureVmCreation = async (options) => {
  const params = buildAzureVmValidationParams(options);
  let data;
  try {
    const response = await api.get("/api/AwsEc2Data/ValidateResourceCreation", {
      params,
      headers: {
        "X-CORRELATION-ID": crypto.randomUUID(),
        accept: "application/json",
      },
    });
    data = response.data;
  } catch (error) {
    console.error("Azure VM creation validation request failed:", error);
    const fieldName = mapAzureVmValidationFields(
      error.response?.data?.fieldName, 
      options.formConfig, 
      options.formData
    );
    
    throw validationError(
      error.response?.data?.reason || error.response?.data?.message ||
        error.message || "Unable to validate the Azure VM. Please try again.",
      fieldName
    );
  }
  if (typeof data?.isValid !== "boolean") {
    console.error("Azure VM validation API returned invalid response:", data);
    throw validationError("The Azure VM validation API returned an invalid response.");
  }
  if (!data.isValid) {
    const fieldName = mapAzureVmValidationFields(
      data.fieldName, 
      options.formConfig, 
      options.formData
    );
    
    throw validationError(
      data.reason || "Azure VM validation failed.",
      fieldName
    );
  }
  return true;
};
